/**
 * TermsPage — full-page terms of service.
 * Accessible at /terms route.
 */

import { BrandLogo } from '../BrandLogo';
import { usePaperTradingStore } from '../../stores/paperTradingStore';

// ---------------------------------------------------------------------------
// Section heading
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="font-display font-bold text-lg text-white">{title}</h2>
      <div className="space-y-2 text-sm text-gray-300 leading-relaxed">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TermsPageProps {
  /**
   * Optional override for the back navigation target — used when the page
   * is reachable while logged out (no dashboard to return to). Defaults to
   * navigating the paper trading store back to the backtesting page.
   */
  onBack?: () => void;
  /** Label for the back button. Defaults to "Back to dashboard". */
  backLabel?: string;
}

export function TermsPage({ onBack, backLabel }: TermsPageProps = {}) {
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);
  const handleBack = onBack ?? (() => setActivePage('backtesting'));
  const label = backLabel ?? 'Back to dashboard';

  return (
    <div className="min-h-screen app-bg py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-10">
        {/* Header */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {label}
          </button>

          <BrandLogo markClassName="w-8 h-8" wordClassName="text-lg" />

          <div>
            <h1 className="font-display font-extrabold text-3xl text-white">Terms of Service</h1>
            <p className="text-sm text-gray-500 mt-1">Last updated: 2026-07-05</p>
          </div>
        </div>

        {/* Content */}
        <div className="glass-panel rounded-2xl p-8 sm:p-10 space-y-8">
          <Section title="Acceptance of Terms">
            <p>
              By accessing or using sharpelab (the "Service"), you agree to be bound by these Terms of Service.
              If you do not agree to these terms, you may not use the Service.
            </p>
            <p>
              These terms constitute a legally binding agreement between you and the sharpelab team.
              Continued use of the Service after any updates constitutes acceptance of the revised terms.
            </p>
          </Section>

          <Section title="Description of Service">
            <p>
              sharpelab is a research and educational backtesting platform for cryptocurrency trading strategies.
              The Service provides tools for strategy creation, backtesting, parameter optimization,
              and paper trading (simulated trading with no real funds).
            </p>
            <p>
              The primary interface for sharpelab is through MCP (Model Context Protocol) connections
              to AI agent clients including Claude Code, Claude Desktop, Codex CLI, and Cursor.
              The web dashboard is provided for reviewing results and managing sessions.
            </p>
          </Section>

          <Section title="No Investment Advice">
            <p className="font-semibold text-white">
              sharpelab does not provide investment advice. All content, strategy results, backtests,
              optimization outputs, and paper trading sessions are provided for educational and research
              purposes only and do not constitute investment, financial, legal, or tax advice.
            </p>
            <p>
              Past performance does not predict future results. Backtested strategy returns are
              hypothetical and may not reflect real-world trading performance due to market impact,
              slippage, execution delays, and other factors.
            </p>
            <p>
              You are solely responsible for all decisions made using this Service. sharpelab team
              members are not registered investment advisors and do not act in any advisory capacity.
            </p>
          </Section>

          <Section title="No Warranty">
            <p>
              The Service is provided "AS IS" and "AS AVAILABLE" without any warranties of any kind,
              whether express or implied. We make no warranties regarding:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>The accuracy, completeness, or timeliness of any market data or calculations</li>
              <li>The availability, uptime, or uninterrupted access to the Service</li>
              <li>The absence of bugs, errors, or inaccuracies in backtest calculations</li>
              <li>The fitness of the Service for any particular purpose</li>
            </ul>
            <p>
              Paper trading is a simulation only — it uses real market data but involves no real funds
              and does not guarantee any real trading outcome.
            </p>
          </Section>

          <Section title="Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, sharpelab and its contributors shall
              not be liable for any direct, indirect, incidental, special, consequential, or punitive
              damages arising from your use of or inability to use the Service.
            </p>
            <p>
              This includes but is not limited to: trading losses, loss of profits, data loss,
              business interruption, or reliance on any strategy results or analysis provided by
              the Service. You use the Service entirely at your own risk.
            </p>
            <p className="font-semibold text-white">
              Without limiting the foregoing, sharpelab, its operators, and contributors shall
              not be responsible or liable, to the maximum extent permitted by law, for any trading
              losses, lost profits, lost opportunities, or other damages of any kind arising out of
              or relating to: (a) any strategy you create, configure, or run on the Service; (b) any
              backtest, grid-search, walk-forward, or other optimization result; (c) any paper-trading
              session or its simulated outcomes; or (d) any decision you make — inside or outside the
              Service — based on any of the foregoing. This limitation applies regardless of the legal
              theory asserted (contract, tort, negligence, or otherwise) and even if sharpelab has
              been advised of the possibility of such damages.
            </p>
          </Section>

          <Section title="Prohibited Jurisdictions">
            <p>
              The Service is <span className="font-semibold text-white">not offered to, and must not be used by,
              residents of the United States</span> or of any sanctioned or high-risk jurisdiction —
              any country or territory that is subject to comprehensive trade sanctions, embargoes,
              or is otherwise designated as a high-risk or prohibited jurisdiction under applicable
              export-control or sanctions law (each, a &quot;Prohibited Jurisdiction&quot;).
            </p>
            <p>
              By accessing or using the Service, you represent and warrant that you are not (a) a
              resident, national, or citizen of, or physically located in, the United States or any
              other Prohibited Jurisdiction, and (b) not otherwise barred from using the Service
              under applicable law. We reserve the right to suspend or terminate access for any
              account we reasonably believe is operating in breach of this representation.
            </p>
            <p>
              <span className="italic">Implementation note:</span> this clause is a contractual
              representation only. Automated IP-based geoblocking is not yet implemented and is
              tracked as a separate follow-up.
            </p>
          </Section>

          <Section title="License to Your Strategy Code">
            <p>
              You retain ownership of the strategy code, parameters, and configurations you create
              or submit to the Service (&quot;Your Strategy Content&quot;). By submitting Your Strategy
              Content, you grant sharpelab a non-exclusive, royalty-free, worldwide, sublicensable,
              and transferable license to access, store, host, execute, analyze, reproduce, and
              otherwise use Your Strategy Content for the purpose of operating, maintaining,
              securing, improving, and developing the Service (including running backtests, grid
              search, walk-forward validation, paper trading, aggregate analytics, and — where we
              have taken reasonable steps to de-identify it — improving our systems and features
              generally).
            </p>
            <p>
              This license does not transfer ownership of Your Strategy Content, and we will not
              publicly disclose Your Strategy Content to other users without your consent. This
              license survives for as long as we retain Your Strategy Content (see Privacy Policy —
              Retention &amp; deletion) and terminates for a given item once it is deleted from the
              Service, except to the extent retained in backups or as required by law.
            </p>
          </Section>

          <Section title="GDPR — Data Protection">
            <p>
              For users in the European Economic Area (EEA) and United Kingdom, we process personal
              data on the following lawful bases: your <span className="font-semibold text-white">consent</span>{' '}
              (e.g. creating an account, connecting Telegram) and our <span className="font-semibold text-white">legitimate
              interest</span> in operating, securing, and improving the Service. We already collect the
              minimum data needed to run the Service (no email address, no real name, no payment
              details) — see the Privacy Policy for the full list.
            </p>
            <p>Subject to applicable law, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li><span className="font-semibold text-white">Access</span> the personal data we hold about you.</li>
              <li><span className="font-semibold text-white">Rectification</span> of inaccurate or incomplete data.</li>
              <li><span className="font-semibold text-white">Erasure</span> of your data (&quot;right to be forgotten&quot;).</li>
              <li><span className="font-semibold text-white">Portability</span> — receive your data in a portable format.</li>
              <li><span className="font-semibold text-white">Objection</span> to processing based on legitimate interest.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us via GitHub issues on the project
              repository, referencing your account username. See the{' '}
              <button
                type="button"
                onClick={() => setActivePage('privacy')}
                className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
              >
                Privacy Policy
              </button>{' '}
              for full details on what we collect and why.
            </p>
          </Section>

          <Section title="Educational and Entertainment Purpose">
            <p>
              sharpelab is created for educational research and entertainment purposes. It is a
              tool for learning about trading strategy design and quantitative analysis — not a
              production trading system.
            </p>
            <p>
              Any use of strategy results, optimization outputs, or analysis from this Service
              to guide real trading decisions is entirely at your own risk and discretion.
              sharpelab team members accept no responsibility for real-money outcomes based
              on use of this Service.
            </p>
          </Section>

          <Section title="Changes to Terms">
            <p>
              We reserve the right to modify these Terms of Service at any time. We will notify
              users of significant changes. If the Service introduces paid features or subscriptions
              in the future, these terms will be updated to reflect new obligations and rights.
            </p>
            <p>
              Your continued use of the Service after any changes constitutes acceptance of the
              updated terms.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              If you have questions about these Terms of Service or the Service in general,
              please reach out via GitHub issues on the project repository.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
